import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';

// Keep this flexible - could be relative in dev or absolute in prod
const API_BASE = window.location.hostname === 'localhost' 
  ? '/api' 
  : 'https://storetoyou.ca/api';

const HomePage = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCities, setSelectedCities] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const cities = ['Kelowna']; // For now, just Kelowna since that's the only city you have
  const valueProps = [
    { title: 'Local Discovery', desc: 'Find unique stores in your neighborhood' },
    { title: 'Easy Search', desc: 'Quick access to products and stores' },
    { title: 'Support Local', desc: 'Help your community thrive' }
  ];

  const toggleCity = (city) => {
    setSelectedCities(prev =>
      prev.includes(city)
        ? prev.filter(c => c !== city)
        : [...prev, city]
    );
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const cityParam = selectedCities.length === 1 
        ? `&city=${selectedCities[0]}` 
        : '';
      
      // Updated URL to use the domain instead of IP
      const response = await fetch(
        `${API_BASE}/search?query=${searchQuery}${cityParam}`
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      
      // Navigate to search results page with the data
      navigate('/search', { 
        state: { 
          results: data, 
          query: searchQuery, 
          cities: selectedCities 
        } 
      });
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="home-container">
      <Header />
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-overlay"></div>
        <div className="container text-center text-white hero-content">
          <h1 className="display-4 fw-bold mb-4">
            Discover Local Stores Near You
          </h1>
          <p className="lead mb-4">
            Find and shop from the best local stores in your city
          </p>
          
          {/* Search Bar with Dropdown */}
          <div className="search-container">
            <form onSubmit={handleSearch} className="d-flex gap-2">
              {/* City Selection Dropdown */}
              <div className="dropdown" style={{ width: '200px' }}>
                <button 
                  type="button"
                  className="btn btn-light dropdown-toggle w-100 text-start" 
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                >
                  {selectedCities.length === 0 
                    ? 'Select cities' 
                    : `${selectedCities.length} selected`}
                </button>
                <ul className={`dropdown-menu w-100 ${isDropdownOpen ? 'show' : ''}`}>
                  {cities.map(city => (
                    <li key={city} className="dropdown-item">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedCities.includes(city)}
                          onChange={() => toggleCity(city)}
                          id={`city-${city}`}
                        />
                        <label className="form-check-label" htmlFor={`city-${city}`}>
                          {city}
                        </label>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Search Input */}
              <div className="input-group">
                <input
                  type="text"
                  className="form-control search-input"
                  placeholder="Search for stores or products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button 
                  type="submit" 
                  className="btn btn-primary search-button"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    'Search'
                  )}
                </button>
              </div>
            </form>

            {/* Selected Cities Tags */}
            {selectedCities.length > 0 && (
              <div className="mt-3 d-flex flex-wrap gap-2">
                {selectedCities.map(city => (
                  <span key={city} className="badge bg-light text-dark">
                    {city}
                    <button
                      type="button"
                      className="btn-close ms-2"
                      style={{ fontSize: '0.5rem' }}
                      onClick={() => toggleCity(city)}
                    ></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Value Proposition Section */}
      <div className="bg-light py-5">
        <div className="container">
          <div className="row">
            {valueProps.map((prop, index) => (
              <div key={index} className="col-md-4 mb-4 mb-md-0">
                <div className="card h-100 border-0 shadow-sm">
                  <div className="card-body text-center p-4">
                    <h3 className="h5 card-title mb-3">{prop.title}</h3>
                    <p className="card-text text-muted">{prop.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Cities Section */}
      <div className="container py-5">
        <h2 className="text-center mb-4">Popular Cities</h2>
        <div className="row">
          <div className="col-md-4 mb-4">
            <div className="card city-card">
              <img 
                src="https://pixabay.com/get/g3fb8ac14e4ad3b2c9fa2bb69f72eef761f3e20eb9339ba172d20d979a68eb0e4c77c7b31518c54bad1e94b559f0e77e3f0d3a0a6d26e5e77b9e7dfda99fe6d84_1280.jpg" 
                className="card-img-top" 
                alt="Kelowna" 
              />
              <div className="card-body">
                <h5 className="card-title">Kelowna</h5>
                <p className="card-text">Discover local stores in Kelowna, BC</p>
                <button 
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setSelectedCities(['Kelowna']);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Explore Kelowna
                </button>
              </div>
            </div>
          </div>
          {/* More cities will be added here later */}
        </div>
      </div>
    </div>
  );
};

export default HomePage;